/**
 * Linear Groups Layout
 */

import groupBy from 'lodash/groupBy'
import { extent } from 'd3-array'
import { getBreakpoint } from '../breakpoints'
import { scaleLinear, scaleBand } from 'd3-scale'
import { getGutters } from './gutters'

export default class GroupsLinear {
  static defaults = {
    // What field is the primary x-axis?
    primary: 'label',
    // What field is the secondary x-axis?
    secondary: 'categoryShortName',
    // How should the layout build the x domain?
    pluckX: d => d.label,
    // How should the layout build the y domain?
    pluckY: d => d.y,
    // How much spacing should be between each item
    margin: 0,
    // How much padding should exist around the entire chart
    // and between each group?
    padding: 0,
    // How should the x-axis by formated?
    formatX: n => n,
    // How should the y-axis by formated?
    formatY: n => n,
    // How tall is the chart?
    height: 100,
    // How wide is the chart?
    width: 100,
    // Is there a x-axis label? Used for calculating spacing
    labelX: '',
    // Is there a y-axis label? Used for calculating spacing
    labelY: '',
    // Are the x-axis ticks vertical?
    vertical: false,
    // Force a minimum Y domain value
    injectY: [],
    // How many ticks on the Y axis?
    ticksY: 6
  }

  constructor(data, params) {
    Object.assign(this, this.constructor.defaults, params)

    this.groups = groupBy(data, this.secondary)

    this.domainX = this.getDomainX(data)
    this.domainY = this.getDomainY(data)

    this.scaleX = this.getScaleX()
    this.scaleY = this.getScaleY()

    this.boundingBox = this.getBoundingBox()

    // Finally, set margins. We need to know what ticks will actually
    // show up, so this has to happen after scale creation
    this.setMargins()

    // Now that we have scales, create a series for each primary field
    this.series = this.getSeries(this.primary, this.secondary)

    // Axis are uppercase. Ensure empty strings
    this.labelX = (this.labelX || '').toUpperCase()
    this.labelY = (this.labelY || '').toUpperCase()
  }

  get hasMultipleSeries() {
    return Object.keys(this.groups).length > 1
  }

  createSeries(data, field, key) {
    let { padding, margin } = this.boundingBox

    let domain = data.map(i => i[field])
    let scale = scaleBand().domain(domain)

    let right = this.scaleX(domain[domain.length - 1]) + this.scaleX.step()
    let left = this.scaleX(domain[0])

    scale.range([left, right])

    scale.paddingInner(margin)
    scale.paddingOuter(padding + margin * 0.5)

    return { key, scale, data }
  }

  getSeries(primary, secondary) {
    return Object.keys(this.groups).map(function(key) {
      return this.createSeries(this.groups[key], primary, key)
    }, this)
  }

  map(fn, scope) {
    return this.series.map(function({ key, scale, data }) {
      return fn.call(scope, data, scale, this.scaleY, key, this)
    }, this)
  }

  getDomainX(data) {
    return data.reduce((memo, next) => {
      return memo.concat(this.pluckX(next))
    }, [])
  }

  getDomainY(data) {
    // Force an upper or lower domain by manually
    // injecting values
    let values = [].concat(this.injectY)

    if (data.length <= 0) {
      values.push(0, 1)
    }

    let domain = data.reduce((memo, next) => {
      return memo.concat(this.pluckY(next))
    }, values)

    return domain
  }

  getScaleX() {
    let scale = scaleBand()

    scale.domain(this.domainX)

    return scale
  }

  getScaleY() {
    let scale = scaleLinear()

    scale.domain(extent(this.domainY))
    scale.nice(6)

    return scale
  }

  getPadding(breakpoint) {
    if (this.hasMultipleSeries === false) {
      return 0
    }

    return breakpoint === 'small' ? this.padding : 0
  }

  setMargins() {
    let { top, bottom, left, right } = this.boundingBox

    this.scaleX.range([left, right])
    this.scaleY.range([bottom, top])
  }

  getTicksX() {
    // scaleBand does not impliment ticks()
    return this.scaleX.domain()
  }

  getTicksY() {
    return this.scaleY.ticks(this.ticksY)
  }

  getBreakpoint() {
    return this.breakpoint || getBreakpoint(this.width)
  }

  getBoundingBox() {
    let name = this.getBreakpoint()

    let { top, right, bottom, left } = getGutters({
      breakpoint: name,
      height: this.height,
      width: this.width,
      bottom: {
        ticks: name === 'small' ? [] : this.getTicksX(),
        format: this.formatX,
        label: this.labelX,
        vertical: this.vertical
      },
      left: {
        ticks: this.getTicksY(),
        format: this.formatY,
        label: this.labelY
      },
      top: {},
      right: {}
    })

    let width = this.width - (right + left)
    let height = this.height - (bottom + top)
    let margin = this.margin
    let padding = this.getPadding(name)

    return {
      top: top,
      right: width + left,
      bottom: height + top,
      left: left,
      width: width,
      height: height,
      margin: margin,
      padding: padding,
      gutterTop: top,
      gutterRight: right,
      gutterBottom: bottom,
      gutterleft: left
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/web-interactive/web-interactive/lib/components/charts/layouts/groups-linear.js